import { FC } from 'react';
import { useTour } from '@/hooks/useTour';
import { useIsMobile } from '@/hooks/useIsMobile';
import DesktopTour from './tour/DesktopTour';
import MobileWelcomeModal from './tour/MobileWelcomeModal';
import type { Config } from 'driver.js';

/**
 * Tour guiado de primeiro acesso (Dashboard).
 *
 * - Desktop (≥1024px): 5 passos com Driver.js + spotlights nos elementos
 *   da sidebar e dashboard.
 * - Mobile (<1024px): um único modal de boas-vindas centralizado, sem
 *   spotlights — direcionando o usuário pro menu hamburger.
 *
 * Em ambos os casos, ao fechar (concluir OU pular) o backend recebe
 * `tutorial_completed_at = now()` via useTour.
 */

const DESKTOP_STEPS: NonNullable<Config['steps']> = [
  {
    popover: {
      title: 'Bem-vindo ao h.lab 👋',
      description:
        'Você vai aprender Inteligência Artificial em trilhas curtas, sem termos complicados. Em uns 2 minutos, te mostro o caminho.',
    },
  },
  {
    element: '[data-tour="trilhas-link"]',
    popover: {
      title: 'Aqui ficam seus cursos',
      description:
        'Trilhas são conjuntos curtos de aulas. Comece pela <b>Trilha 1 — Fundamentos de IA</b>: pode fazer no seu ritmo, parar e voltar.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="profile-tile"]',
    popover: {
      title: 'Você ganha XP completando aulas',
      description:
        'Cada aula concluída vira XP. Quando enche a barra, você sobe de nível. Não é obrigatório — é só pra dar aquele estímulo.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="ranking-tile"]',
    popover: {
      title: 'Seu setor compete junto',
      description:
        'Você não corre sozinho. O XP que você ganha conta pro seu setor no ranking. Dê uma olhada em <b>Ranking</b> pra ver como vocês estão.',
      side: 'bottom',
    },
  },
  {
    popover: {
      title: 'Tá pronto! 🚀',
      description:
        'Bora começar pela <b>Trilha 1 — Fundamentos de IA</b>? Você pode refazer este tour quando quiser em <b>Meu Perfil</b>.',
    },
  },
];

const TourGuide: FC = () => {
  const { shouldStart, finishTour, skipTour } = useTour();
  const isMobile = useIsMobile();

  if (!shouldStart) return null;

  return isMobile ? (
    <MobileWelcomeModal
      onComplete={finishTour}
      onSkip={skipTour}
      content={{
        emoji: '👋',
        title: 'Bem-vindo ao h.lab',
        subtitle: 'Aprenda Inteligência Artificial em trilhas curtas, sem termos complicados.',
        tipTitle: 'Pra começar:',
        tipBody: (
          <>
            Toque no botão <b className="text-accent">☰</b> no canto superior pra abrir o menu,
            depois escolha <b className="text-content-primary">Trilhas</b>. Recomendamos começar
            pela <b className="text-content-primary">Trilha 1 — Fundamentos de IA</b>.
          </>
        ),
        ctaLabel: 'Começar',
      }}
    />
  ) : (
    <DesktopTour steps={DESKTOP_STEPS} onComplete={finishTour} onSkip={skipTour} />
  );
};

export default TourGuide;
